"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.authInterceptor = exports.authInterceptorFactory = exports.isDefinitiveAuthRejection = exports.isDefinitiveRefreshBodyFailure = exports.isInfrastructureRefreshFailure = exports.isTransportAuthFailure = exports.isWafHttpResponse = void 0;
/** Shared WAF-block detection for interceptor and save error handlers. */
function isWafHttpResponse(rejection) {
    var _a;
    if ((rejection === null || rejection === void 0 ? void 0 : rejection.status) !== 403) {
        return false;
    }
    var body = rejection.data;
    if (typeof body === 'string') {
        return (body.includes('<title>403 Forbidden</title>') ||
            body.includes('ModSecurity') ||
            body.includes('Request blocked'));
    }
    return ((_a = rejection.headers) === null || _a === void 0 ? void 0 : _a.call(rejection, 'Server')) === 'nginx';
}
exports.isWafHttpResponse = isWafHttpResponse;
/**
 * Network / gateway failures — NOT proof the refresh cookie is invalid.
 * Clearing JWT here nukes offline mode when the host is down or flaky.
 */
function isTransportAuthFailure(error) {
    if (!error) {
        return true;
    }
    var status = error.status;
    // Angular $http: 0 = network error / CORS / reset; -1 sometimes used for abort/timeout
    if (status === 0 || status === -1 || status == null) {
        return true;
    }
    if (status === 408 || status === 502 || status === 503 || status === 504) {
        return true;
    }
    // Do not use navigator.onLine — it misses degraded nets (DNS, captive portal, black-hole).
    // Transport class is decided only from HTTP/status outcomes above.
    return false;
}
exports.isTransportAuthFailure = isTransportAuthFailure;
/**
 * App-level body when the server is up enough to reply but DB/infra is down.
 * Must NOT clear JWT / show login — same as transport failure.
 * Example: { Result: "ERROR", Error: "DB_CONNECTION_FAILED", Message: "Database connection unavailable..." }
 */
function isInfrastructureRefreshFailure(data) {
    if (!data || typeof data !== 'object') {
        return false;
    }
    var code = String(data.Error || data.Code || data.error || '').toUpperCase();
    if (code === 'DB_CONNECTION_FAILED' ||
        code === 'DATABASE_ERROR' ||
        code === 'SERVICE_UNAVAILABLE' ||
        code === 'DB_UNAVAILABLE') {
        return true;
    }
    var msg = String(data.Message || data.message || data.error || '').toLowerCase();
    if (msg.includes('database connection') ||
        msg.includes('database unavailable') ||
        msg.includes('connection unavailable') ||
        (msg.includes('try again later') && msg.includes('unavailable'))) {
        return true;
    }
    return false;
}
exports.isInfrastructureRefreshFailure = isInfrastructureRefreshFailure;
/**
 * Refresh response body that means the refresh cookie/token is invalid — log out.
 * Does not include infra/DB errors.
 */
function isDefinitiveRefreshBodyFailure(data) {
    var _a;
    if (!data || typeof data !== 'object') {
        return false;
    }
    if (data.Result === 'OK' && ((_a = data.Data) === null || _a === void 0 ? void 0 : _a.accessToken)) {
        return false;
    }
    if (isInfrastructureRefreshFailure(data)) {
        return false;
    }
    var code = String(data.Error || data.Code || data.error || '').toUpperCase();
    if (code === 'INVALID_REFRESH_TOKEN' ||
        code === 'MISSING_REFRESH_TOKEN' ||
        code === 'EXPIRED_REFRESH_TOKEN' ||
        code === 'REVOKED' ||
        code === 'REVOKED_REFRESH_TOKEN') {
        return true;
    }
    var result = String(data.Result || '').toUpperCase();
    if (result !== 'ERROR' && result !== 'FAIL' && result !== 'EXCEPTION' && result !== 'AUTH ERROR') {
        return false;
    }
    var msg = String(data.Message || data.message || data.error || '').toLowerCase();
    return (msg.includes('refresh token') ||
        msg.includes('invalid or expired') ||
        msg.includes('token required') ||
        msg.includes('not authenticated') ||
        msg.includes('please log in') ||
        (msg.includes('expired') && msg.includes('token')) ||
        (msg.includes('invalid') && msg.includes('token')));
}
exports.isDefinitiveRefreshBodyFailure = isDefinitiveRefreshBodyFailure;
/** Server explicitly rejected the session (invalid/expired refresh, revoked, etc.). */
function isDefinitiveAuthRejection(error) {
    if (!error || isTransportAuthFailure(error)) {
        return false;
    }
    if (isInfrastructureRefreshFailure(error.data)) {
        return false;
    }
    var status = error.status;
    if (status === 401 || status === 403) {
        return true;
    }
    // Application-level body that means refresh token is bad (not DB/infra)
    if (error.data && isDefinitiveRefreshBodyFailure(error.data)) {
        return true;
    }
    return false;
}
exports.isDefinitiveAuthRejection = isDefinitiveAuthRejection;
function buildWafTriggerHint(requestData) {
    var _a, _b;
    if (!requestData || typeof requestData !== 'object') {
        return '';
    }
    if (requestData.action !== 'updateRow') {
        return '';
    }
    var bodyText = ((_a = requestData.row) === null || _a === void 0 ? void 0 : _a.pnBody) || ((_b = requestData.reply) === null || _b === void 0 ? void 0 : _b.pnBody);
    if (!bodyText) {
        return '';
    }
    return 'Your note contains words that look like programming commands (e.g., "watch", "clone", "redis"). ';
}
function authInterceptorFactory($q, $injector) {
    var isRefreshing = false;
    var failedQueue = [];
    function processQueue(error, token) {
        if (token === void 0) { token = null; }
        var $http = $injector.get('$http');
        failedQueue.forEach(function (_a) {
            var config = _a.config, deferred = _a.deferred;
            if (error) {
                deferred.reject(error);
            }
            else {
                if (token) {
                    config.headers = config.headers || {};
                    config.headers.Authorization = 'Bearer ' + token;
                }
                $http(config).then(function (response) { return deferred.resolve(response); }, function (retryError) { return deferred.reject(retryError); });
            }
        });
        failedQueue.length = 0;
    }
    function getAccessToken() {
        try {
            return localStorage.getItem('jwt_token');
        }
        catch (_a) {
            return null;
        }
    }
    function setAccessToken(token) {
        try {
            localStorage.setItem('jwt_token', token);
        }
        catch (error) {
            console.error('Failed to store JWT token:', error);
        }
    }
    function clearAccessToken() {
        try {
            localStorage.removeItem('jwt_token');
        }
        catch (error) {
            console.error('Failed to clear JWT token:', error);
        }
    }
    function addAuthHeader(config) {
        var _a;
        var token = getAccessToken();
        if (token && !((_a = config.headers) === null || _a === void 0 ? void 0 : _a.Authorization)) {
            config.headers = config.headers || {};
            config.headers.Authorization = 'Bearer ' + token;
        }
    }
    function refreshAccessToken() {
        var $http = $injector.get('$http');
        return $http({
            method: 'POST',
            url: '/api/api.php',
            data: {
                action: 'refreshAuthToken',
            },
            headers: { 'Content-Type': 'application/json' },
            __isRetry: true,
            // Prefer not hanging forever when the box is dying
            timeout: 15000,
        });
    }
    function broadcast(name, payload) {
        try {
            var $rootScope_1 = $injector.get('$rootScope');
            var $timeout_1 = $injector.get('$timeout');
            $timeout_1(function () { return $rootScope_1.$broadcast(name, payload); }, 0);
        }
        catch (_a) {
            /* ignore */
        }
    }
    function showWafBlockNotification(rejection) {
        var _a, _b, _c;
        var url = ((_a = rejection.config) === null || _a === void 0 ? void 0 : _a.url) || 'unknown';
        var method = ((_b = rejection.config) === null || _b === void 0 ? void 0 : _b.method) || 'unknown';
        var requestData = (_c = rejection.config) === null || _c === void 0 ? void 0 : _c.data;
        var triggerHint = buildWafTriggerHint(requestData);
        var message = {
            title: '🛡️ Security Filter Blocked Your Request',
            details: "".concat(triggerHint, "The Web Application Firewall (WAF) flagged your request as potentially unsafe."),
            technicalInfo: "Request: ".concat(method, " ").concat(url),
            actions: [
                'Your draft was saved locally — open edit and tap Restore',
                'Try rephrasing technical terms in your text',
                'Contact admin if this keeps happening',
            ],
            timestamp: new Date().toISOString(),
        };
        console.error('🛡️ WAF BLOCK DETECTED:', message);
        broadcast('waf:blocked', message);
    }
    return {
        request: function (config) {
            addAuthHeader(config);
            return config;
        },
        responseError: function (rejection) {
            var _a, _b;
            if (isWafHttpResponse(rejection)) {
                console.error('WAF block detected for:', (_a = rejection.config) === null || _a === void 0 ? void 0 : _a.url);
                showWafBlockNotification(rejection);
                return $q.reject(rejection);
            }
            if (rejection.status === 401 && !((_b = rejection.config) === null || _b === void 0 ? void 0 : _b.__isRetry)) {
                if (!isRefreshing) {
                    isRefreshing = true;
                    refreshAccessToken().then(function (response) {
                        var _a, _b, _c, _d, _e, _f, _g;
                        isRefreshing = false;
                        if (((_a = response.data) === null || _a === void 0 ? void 0 : _a.Result) === 'OK' && ((_c = (_b = response.data) === null || _b === void 0 ? void 0 : _b.Data) === null || _c === void 0 ? void 0 : _c.accessToken)) {
                            var newToken = response.data.Data.accessToken;
                            setAccessToken(newToken);
                            broadcast('auth:tokenRefreshed', { token: newToken });
                            processQueue(null, newToken);
                            return;
                        }
                        var failMsg = String(((_d = response.data) === null || _d === void 0 ? void 0 : _d.Message) || ((_e = response.data) === null || _e === void 0 ? void 0 : _e.error) || 'Refresh failed');
                        // HTTP 200 + DB/infra error (e.g. pilotUI DB_CONNECTION_FAILED) — keep session
                        if (isInfrastructureRefreshFailure(response.data)) {
                            console.warn('[auth] refresh infrastructure failure — keeping access token for offline use', failMsg);
                            broadcast('auth:serverUnreachable', {
                                reason: 'refresh_infra',
                                message: failMsg,
                                code: ((_f = response.data) === null || _f === void 0 ? void 0 : _f.Error) || ((_g = response.data) === null || _g === void 0 ? void 0 : _g.Code),
                            });
                            processQueue({
                                status: response.status || 503,
                                data: response.data,
                                config: rejection.config,
                            }, null);
                            return;
                        }
                        // Only clear JWT when the body says refresh token is invalid/missing
                        if (isDefinitiveRefreshBodyFailure(response.data)) {
                            console.warn('[auth] refresh rejected by server:', failMsg);
                            clearAccessToken();
                            broadcast('auth:sessionEnded', { reason: 'refresh_rejected', message: failMsg });
                            processQueue(rejection, null);
                            return;
                        }
                        // Unknown app-level ERROR: prefer keep session over login form
                        console.warn('[auth] refresh non-OK body — keeping token', failMsg);
                        broadcast('auth:serverUnreachable', {
                            reason: 'refresh_non_ok',
                            message: failMsg,
                        });
                        processQueue({
                            status: response.status || 503,
                            data: response.data,
                            config: rejection.config,
                        }, null);
                    }, function (error) {
                        var _a;
                        isRefreshing = false;
                        if (isTransportAuthFailure(error) || isInfrastructureRefreshFailure(error === null || error === void 0 ? void 0 : error.data)) {
                            // Keep jwt_token — offline / degraded mode; do not force login
                            console.warn('[auth] refresh transport/infra failure — keeping access token for offline use', error && error.status);
                            broadcast('auth:serverUnreachable', {
                                reason: isInfrastructureRefreshFailure(error === null || error === void 0 ? void 0 : error.data)
                                    ? 'refresh_infra'
                                    : 'refresh_transport',
                                status: error && error.status,
                                message: (_a = error === null || error === void 0 ? void 0 : error.data) === null || _a === void 0 ? void 0 : _a.Message,
                            });
                            // Reject queued calls as unreachable, but session stays
                            processQueue(error || rejection, null);
                            return;
                        }
                        if (isDefinitiveAuthRejection(error)) {
                            console.warn('[auth] refresh definitive auth failure — clearing session');
                            clearAccessToken();
                            broadcast('auth:sessionEnded', {
                                reason: 'refresh_auth_failed',
                                status: error && error.status,
                            });
                            processQueue(error, null);
                            return;
                        }
                        // Unknown error: prefer keep session over nuking offline UX
                        console.warn('[auth] refresh unknown error — keeping token', error);
                        broadcast('auth:serverUnreachable', {
                            reason: 'refresh_unknown',
                            status: error && error.status,
                        });
                        processQueue(error || rejection, null);
                    });
                }
                var deferred = $q.defer();
                failedQueue.push({ config: rejection.config, deferred: deferred });
                return deferred.promise;
            }
            return $q.reject(rejection);
        },
    };
}
exports.authInterceptorFactory = authInterceptorFactory;
authInterceptorFactory.$inject = ['$q', '$injector'];
exports.authInterceptor = authInterceptorFactory;
